import { App, Button, Space, Table } from "antd";
import { protectedResources } from "config/msalConfig";
import execute from "hooks/execute";
import { useEffect, useState } from "react";
import { FaEnvelope, FaFilePen, FaPlus, FaTrash } from "react-icons/fa6";
import { CustomerUserEditor } from "./CustomerUserEditor";
import DeleteConfirmationModal from "components/shared/DeleteConfirmationModal";
import ShareInvitationModal from "components/shared/ShareInvitationModal";

const CustomerUserData = ({ customer, highlightUserId }) => {
    const { message } = App.useApp();
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editableUser, setEditableUser] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [inviteInfo, setInviteInfo] = useState({ name: '', email: '', link: '' });
    const [invitedUserId, setInvitedUserId] = useState(null);
    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: 5,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '15', '20'],
            locale: { items_per_page: '' },
        },
    });

    const toBoolean = (value) => value === true || value === 1 || value === '1';

    const columns = [
        {
            title: 'Created On',
            dataIndex: 'CreatedOn',
            key: 'CreatedOn',
            render: (text) => text ? new Date(text).toLocaleDateString() : '',
            sorter: (a, b) => new Date(a.CreatedOn) - new Date(b.CreatedOn)
        },
        {
            title: 'First Name',
            dataIndex: 'FirstName',
            key: 'FirstName',
            sorter: (a, b) => (a.FirstName || '').localeCompare(b.FirstName || '')
        },
        {
            title: 'Last Name',
            dataIndex: 'LastName',
            key: 'LastName',
            sorter: (a, b) => (a.LastName || '').localeCompare(b.LastName || '')
        },
        {
            title: 'Email',
            dataIndex: 'Email',
            key: 'Email',
            sorter: (a, b) => (a.Email || '').localeCompare(b.Email || '')
        },
        {
            title: 'Admin',
            dataIndex: 'IsAdmin',
            key: 'IsAdmin',
            render: (isAdmin) => isAdmin ? 'Yes' : 'No',
            sorter: (a, b) => a.IsAdmin - b.IsAdmin
        },
        {
            title: 'Status',
            dataIndex: 'IsActive',
            key: 'IsActive',
            render: (_, record) => {
                const isOnboarded = toBoolean(record?.IsB2COnboarded);
                const isActive = toBoolean(record?.IsActive);
                const statusText = !isActive ? 'Inactive' : (!isOnboarded ? 'Pending' : 'Active');
                const bgColor = !isActive ? '#808080' : (!isOnboarded ? '#fa8c16' : '#4CAF50');

                return (
                    <span
                        style={{
                            display: 'inline-block',
                            width: '88px',
                            padding: '2px 0',
                            height: '25px',
                            lineHeight: '21px',
                            color: 'white',
                            textAlign: 'center',
                            borderRadius: '5px',
                            backgroundColor: bgColor
                        }}
                    >
                        {statusText}
                    </span>
                );
            },
            sorter: (a, b) => {
                const rank = (record) => {
                    const onboarded = toBoolean(record?.IsB2COnboarded);
                    const active = toBoolean(record?.IsActive);
                    if (!active) return 0;
                    if (!onboarded) return 1;
                    return 2;
                };
                return rank(a) - rank(b);
            }
        },
        {
            title: 'Modules',
            dataIndex: 'Modules',
            key: 'Modules',
            render: (modules) => modules || 'None'
        },
        {
            title: (
                <Button
                    color="default"
                    variant="text"
                    onClick={openEditorToAdd}
                    icon={<FaPlus style={{ color: '#ffad1c' }} />}
                    title="Add New User"
                />
            ),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        color="default"
                        variant="text"
                        title="Send Invitation"
                        onClick={() => handleSendInvitation(record)}
                        icon={<FaEnvelope style={{ color: '#ffad1c' }} />}
                    />
                    <Button
                        color="default"
                        variant="text"
                        title="Edit User"
                        onClick={() => openEditorToEdit(record)}
                        icon={<FaFilePen style={{ color: '#ffad1c' }} />}
                    />
                    <Button
                        color="default"
                        variant="text"
                        title="Delete User"
                        onClick={() => handleDelete(record)}
                        icon={<FaTrash style={{ color: '#ffad1c' }} />}
                    />
                </Space>
            )
        }
    ];

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/customerUsers/customer/${customer.Customer_ID}`
            );
            console.log('Customer users response:', response);
            if (response.success && response.data) {
                setUsers(Array.isArray(response.data) ? response.data : [response.data]);
            } else {
                setUsers([]);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching customer users:', err);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (customer && customer.Customer_ID) {
            fetchData();
        }
    }, [customer]);

    // Auto-navigate to the page containing the highlighted user
    useEffect(() => {
        if (highlightUserId && users.length > 0) {
            const userIndex = users.findIndex(user => user.UserID === highlightUserId);
            if (userIndex !== -1) {
                const pageSize = tableParams.pagination.pageSize;
                const targetPage = Math.floor(userIndex / pageSize) + 1;
                if (tableParams.pagination.current !== targetPage) {
                    setTableParams({
                        ...tableParams,
                        pagination: {
                            ...tableParams.pagination,
                            current: targetPage
                        }
                    });
                }
            }
        }
    }, [highlightUserId, users, tableParams.pagination.pageSize]);

    function openEditorToAdd() {
        setEditableUser({
            Customer_ID: customer.Customer_ID,
            Org_ID: customer.Org_ID,
            Domain: customer.Domain
        });
        setIsEditorOpen(true);
    }

    function openEditorToEdit(user) {
        setEditableUser(user);
        setIsEditorOpen(true);
    }

    const handleSendInvitation = async (user) => {
        try {
            const response = await execute(
                'POST',
                `${protectedResources.api.baseEndPoint}/customerUsers/sendSignupInvitation`,
                { userId: user.UserID, sendEmail: false }
            );
            if (response.success) {
                const data = response.data || {};
                setInviteInfo({
                    name: data.customerName || `${user.FirstName || ''} ${user.LastName || ''}`.trim(),
                    email: data.email || user.Email || '',
                    link: data.inviteLink || ''
                });
                setInvitedUserId(user.UserID);
                setInviteModalOpen(true);
            } else {
                message.error(response.message || 'Failed to send invitation');
            }
        } catch (error) {
            message.error('Failed to send invitation: ' + error.message);
        }
    };

    const handleCopyInvite = async () => {
        try { await navigator.clipboard.writeText(inviteInfo.link || ''); } catch (_) {}
    };

    const handleResendEmail = async () => {
        const response = await execute(
            'POST',
            `${protectedResources.api.baseEndPoint}/customerUsers/sendSignupInvitation`,
            { userId: invitedUserId, inviteLink: inviteInfo.link, sendEmail: true }
        );
        if (!response.success) {
            throw new Error(response.message || 'Failed to send invitation');
        }
        return true;
    };

    const handleDelete = (user) => {
        setCurrentRecord(user);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            const response = await execute(
                'DELETE',
                `${protectedResources.api.baseEndPoint}/customerUsers/${currentRecord.UserID}`
            );
            if (response.success) {
                message.success('User status updated successfully');
                await fetchData();
            } else {
                message.error(response.message || 'Failed to update user');
            }
        } catch (error) {
            message.error('Failed to update user: ' + error.message);
        }
        setIsDeleteModalOpen(false);
    };

    const handleUserSaved = () => {
        setIsEditorOpen(false);
        fetchData();
    };

    const handleEditorCancelled = () => {
        setIsEditorOpen(false);
    };

    const handleTableChange = (pagination) => {
        setTableParams({
            pagination,
        });
    };

    return (
        <>
            <style>
                {`
                    /* Aggressively hide ALL inputs in page size dropdown */
                    input[type="search"],
                    .ant-select-dropdown input,
                    .ant-select input,
                    .ant-pagination input,
                    .ant-pagination-options input,
                    .ant-pagination-options .ant-select input,
                    .ant-pagination-options .ant-select-selection-search,
                    .ant-pagination-options .ant-select-selection-search-input,
                    .ant-pagination-options .ant-select-selector input,
                    .ant-select-dropdown .ant-select-selection-search,
                    .ant-select-dropdown input[type="search"],
                    .rc-virtual-list input,
                    div[class*="pagination"] input,
                    div[class*="select"] input[type="search"] {
                        display: none !important;
                        visibility: hidden !important;
                        width: 0px !important;
                        height: 0px !important;
                        opacity: 0 !important;
                        pointer-events: none !important;
                        position: absolute !important;
                        left: -9999px !important;
                    }
                    .ant-pagination-options .ant-select-selector {
                        cursor: pointer !important;
                    }
                    /* Highlight searched user row */
                    .highlighted-user-row {
                        background-color: #fff7e6 !important;
                        border-left: 3px solid #ffad1c !important;
                    }
                    .highlighted-user-row:hover {
                        background-color: #ffe7ba !important;
                    }
                `}
            </style>
            <Table
                rowKey={record => record.UserID}
                columns={columns}
                dataSource={users}
                loading={loading}
                pagination={{
                    ...tableParams.pagination,
                    showSizeChanger: true,
                    pageSizeOptions: ['5', '10', '15', '20'],
                    locale: { items_per_page: '' },
                    showQuickJumper: false,
                }}
                onChange={handleTableChange}
                size="small"
                rowClassName={(record) =>
                    highlightUserId && record.UserID === highlightUserId ? 'highlighted-user-row' : ''
                }
            />
            {isEditorOpen && (
                <CustomerUserEditor
                    isOpen={isEditorOpen}
                    user={editableUser}
                    customer={customer}
                    onUserSaved={handleUserSaved}
                    onUserCancelled={handleEditorCancelled}
                />
            )}
            {inviteModalOpen && (
                <ShareInvitationModal
                    isOpen={inviteModalOpen}
                    name={inviteInfo.name}
                    email={inviteInfo.email}
                    inviteLink={inviteInfo.link}
                    onCopyLink={handleCopyInvite}
                    onSendEmail={handleResendEmail}
                    onClose={() => setInviteModalOpen(false)}
                />
            )}
            {isDeleteModalOpen && (
                <DeleteConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    record={currentRecord}
                    onDeleteConfirmed={handleConfirmDelete}
                />
            )}
        </>
    );
};

export default CustomerUserData;
